<script setup lang="ts" generic="TKey extends string">
import { nextTick } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import type { RequestDetailTab } from '../../types/listingRequestTypes'

// Icon tab strip for the request-detail screen. Module-local rather than the kit
// `Tabs`: that one is a sliding segmented control without icons, and this design
// is a bordered strip with a tinted active pill. Follows the WAI-ARIA tabs
// pattern — arrow keys move between tabs, only the active one is in tab order.
const props = defineProps<{
  tabs: RequestDetailTab<TKey>[]
  /** Id prefix shared with the panel, so each tab can name the panel it controls. */
  idPrefix: string
}>()

const activeKey = defineModel<TKey>({ required: true })

function tabIdOf(key: string): string {
  return `${props.idPrefix}-tab-${key}`
}

function focusTab(index: number) {
  const count = props.tabs.length
  const tab = props.tabs[(index + count) % count]
  if (!tab) return
  activeKey.value = tab.key
  // The roving tabindex moves with the active key, so focus after it re-renders.
  void nextTick(() => document.getElementById(tabIdOf(tab.key))?.focus())
}

function handleKeydown(event: KeyboardEvent, index: number) {
  const moves: Record<string, number> = {
    ArrowRight: index + 1,
    ArrowLeft: index - 1,
    Home: 0,
    End: props.tabs.length - 1,
  }
  const target = moves[event.key]
  if (target === undefined) return
  event.preventDefault()
  focusTab(target)
}
</script>

<template>
  <Box flex align="center" gap="3" role="tablist" class="detail-tabs">
    <Box
      v-for="(tab, index) in tabs"
      :id="tabIdOf(tab.key)"
      :key="tab.key"
      is="button"
      type="button"
      role="tab"
      flex
      align="center"
      gap="2"
      class="detail-tabs__tab"
      :class="{ 'detail-tabs__tab--active': activeKey === tab.key }"
      :aria-selected="activeKey === tab.key"
      :aria-controls="`${idPrefix}-panel`"
      :tabindex="activeKey === tab.key ? 0 : -1"
      @click="activeKey = tab.key"
      @keydown="handleKeydown($event, index)"
    >
      <PiIcon :name="tab.icon" class="detail-tabs__icon" />
      <Text
        is="span"
        type="body"
        :weight="activeKey === tab.key ? 'semibold' : 'medium'"
        color="inherit"
        class="detail-tabs__label"
      >
        {{ tab.label }}
      </Text>
      <Box
        v-if="tab.count"
        is="span"
        flex
        align="center"
        justify="center"
        class="detail-tabs__count"
        :aria-label="`${tab.count} items`"
      >
        <Text is="span" type="caption" weight="bold" color="inverse">{{ tab.count }}</Text>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.detail-tabs {
  align-self: flex-start;
  max-width: 100%;
  padding: 0.375rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: 12px;
  background: var(--p-surface-0);
  overflow-x: auto;
  scrollbar-width: none;
}

.detail-tabs::-webkit-scrollbar {
  display: none;
}

.detail-tabs__tab {
  flex-shrink: 0;
  height: 2.25rem;
  padding: 0 0.875rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--p-text-muted-color);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
}

.detail-tabs__tab:hover {
  color: var(--p-text-color);
}

.detail-tabs__tab:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

.detail-tabs__tab--active,
.detail-tabs__tab--active:hover {
  background: var(--p-primary-100);
  color: var(--p-primary-500);
}

.detail-tabs__icon {
  font-size: 0.875rem;
}

.detail-tabs__count {
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.25rem;
  border: 2px solid var(--p-primary-100);
  border-radius: 999px;
  background: var(--p-primary-500);
  box-sizing: border-box;
}

.detail-tabs__label {
  line-height: normal;
  white-space: nowrap;
}

@media (max-width: 992px) {
  .detail-tabs {
    align-self: stretch;
  }

  .detail-tabs__tab {
    height: 2.75rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .detail-tabs__tab {
    transition: none;
  }
}
</style>
